import { create } from 'zustand';
import { mostRecentSessionDate } from '../Utils/marketHours';

const STORAGE_KEY = 'spx_chart_price_window_v1';

export function normalizePriceWindow(range) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return null;
  const tradingDate = typeof range?.tradingDate === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(range.tradingDate)
    ? range.tradingDate
    : null;
  return tradingDate ? { from, to, tradingDate } : { from, to };
}

function samePriceWindow(left, right) {
  return Number.isFinite(left?.from)
    && Number.isFinite(left?.to)
    && Number.isFinite(right?.from)
    && Number.isFinite(right?.to)
    && Math.abs(left.from - right.from) <= 1e-6
    && Math.abs(left.to - right.to) <= 1e-6
    && (left.tradingDate ?? null) === (right.tradingDate ?? null);
}

export function loadChartPriceWindowPreference(liveTradingDate) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const preference = normalizePriceWindow(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    if (preference?.tradingDate) {
      const currentLiveTradingDate = liveTradingDate ?? mostRecentSessionDate();
      if (preference.tradingDate !== currentLiveTradingDate) return null;
    }
    return preference;
  } catch {
    return null;
  }
}

// Keep the first gesture write immediate. Coalesce later wheel frames into one
// trailing storage write because localStorage is synchronous.
const PERSIST_COALESCE_MS = 250;
let persistTimer = null;
let pendingPreference;

function writePreference(preference) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (preference) localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage is optional. The in-memory viewport remains shared.
  }
}

function flushPreference() {
  persistTimer = null;
  if (pendingPreference === undefined) return;
  const preference = pendingPreference;
  pendingPreference = undefined;
  writePreference(preference);
}

function savePreference(preference) {
  pendingPreference = preference;
  if (persistTimer) return;
  writePreference(preference);
  pendingPreference = undefined;
  persistTimer = setTimeout(flushPreference, PERSIST_COALESCE_MS);
}

export function flushChartPriceWindowPersist() {
  if (persistTimer) clearTimeout(persistTimer);
  flushPreference();
}

export const useChartPriceWindowStore = create((set, get) => ({
  preference: loadChartPriceWindowPreference(),
  source: null,
  replayWindowActive: false,
  preReplayPreference: null,
  replayTradingDate: null,
  replayPreferencesByDate: {},

  publishUserRange: (source, range, { tradingDate } = {}) => {
    const state = get();
    const preference = normalizePriceWindow({
      ...range,
      tradingDate: tradingDate
        ?? range?.tradingDate
        ?? (state.replayWindowActive ? state.replayTradingDate : null),
    });
    if (!preference) return false;
    if (samePriceWindow(state.preference, preference) && state.source === source) return false;
    if (state.replayWindowActive && state.replayTradingDate) {
      set({
        preference,
        source,
        replayPreferencesByDate: {
          ...state.replayPreferencesByDate,
          [state.replayTradingDate]: preference,
        },
      });
    } else {
      set({ preference, source });
      savePreference(preference);
    }
    return true;
  },

  reset: (source) => {
    const state = get();
    if (state.preference == null && state.source === source) return false;
    if (state.replayWindowActive && state.replayTradingDate) {
      set({
        preference: null,
        source,
        replayPreferencesByDate: {
          ...state.replayPreferencesByDate,
          [state.replayTradingDate]: null,
        },
      });
    } else {
      set({ preference: null, source });
      savePreference(null);
    }
    return true;
  },

  followReplayDate: (replayTradingDate) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(replayTradingDate || '')) return false;
    const state = get();
    if (state.replayWindowActive && state.replayTradingDate === replayTradingDate) return false;

    const replayPreferencesByDate = { ...state.replayPreferencesByDate };
    if (state.replayWindowActive && state.replayTradingDate) {
      replayPreferencesByDate[state.replayTradingDate] = state.preference;
    }
    const preference = Object.prototype.hasOwnProperty.call(
      replayPreferencesByDate,
      replayTradingDate,
    )
      ? replayPreferencesByDate[replayTradingDate]
      : null;
    set({
      preference,
      source: 'replay',
      replayWindowActive: true,
      preReplayPreference: state.replayWindowActive
        ? state.preReplayPreference
        : state.preference,
      replayTradingDate,
      replayPreferencesByDate,
    });
    return true;
  },

  endReplayWindow: (liveTradingDate) => {
    const state = get();
    if (!state.replayWindowActive) return false;
    const replayPreferencesByDate = { ...state.replayPreferencesByDate };
    if (state.replayTradingDate) {
      replayPreferencesByDate[state.replayTradingDate] = state.preference;
    }
    const preReplayPreference = normalizePriceWindow(state.preReplayPreference);
    const validLiveTradingDate = /^\d{4}-\d{2}-\d{2}$/.test(liveTradingDate || '');
    const preference = preReplayPreference
      && validLiveTradingDate
      && (!preReplayPreference.tradingDate
        || preReplayPreference.tradingDate === liveTradingDate)
      ? { ...preReplayPreference, tradingDate: liveTradingDate }
      : null;
    set({
      preference,
      source: 'replay-exit',
      replayWindowActive: false,
      preReplayPreference: null,
      replayTradingDate: null,
      replayPreferencesByDate,
    });
    savePreference(preference);
    return true;
  },
}));

export const CHART_PRICE_WINDOW_STORAGE_KEY = STORAGE_KEY;
